// =============================================================================
// API Types — snake_case to match JSON wire format
// =============================================================================

// --- Auth ---

export interface LoginRequest {
  username: string;
  passcode: string;
}

export interface AdminPubkeyInfo {
  label: string;
  pem: string;
  fingerprint: string;
}

export interface TokenResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  encryption_salt: string | null;
  is_admin?: boolean;
  name?: string;
  help_view_count?: number;
  scheme_version?: string | null;
  wrapped_dek?: string | null;
  password_salt?: string | null;
  admin_pubkeys?: AdminPubkeyInfo[] | null;
  admin_rewrap_required?: boolean;
  admin_debug_enabled?: boolean;
}

export interface RegisterRequest {
  invite_code: string;
  email: string;
  password: string;
}

export interface RefreshRequest {
  refresh_token: string;
}

export interface RefreshResponse {
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  encryption_salt: string | null;
  is_admin?: boolean;
  name?: string;
  help_view_count?: number;
  scheme_version?: string | null;
  wrapped_dek?: string | null;
  password_salt?: string | null;
  admin_pubkeys?: AdminPubkeyInfo[] | null;
  admin_rewrap_required?: boolean;
  admin_debug_enabled?: boolean;
}

// --- v2 wrapped-DEK auth ---

export interface RecoveryPayloadWire {
  code: string;
  recovery_salt: string;
  wrapped_dek: string;
}

export interface AdminWrappedDekWire {
  fingerprint: string;
  wrapped_dek: string;
}

export interface RecoverVerifyResponse {
  recovery_salt: string;
  wrapped_dek: string;
}

export interface ForgotPasswordRequest {
  email: string;
}

export interface ResetPasswordRequest {
  email: string;
  code: string;
  new_password: string;
}

// --- Providers ---

export interface ModelByMode {
  model: string;
  label: string;
}

export interface ConfigurableParameter {
  key: string;
  label: string;
  type: string;
  options?: string[];
  min?: number;
  max?: number;
  default?: number | string;
  default_by_mode?: Record<string, unknown>;
  min_by_mode?: Record<string, number>;
  max_by_mode?: Record<string, number>;
  requires_capability?: string;
  group?: string;
  group_priority?: Record<string, number>;
  provider_note?: string;
}

export interface Capabilities {
  streaming: boolean;
  web_search: boolean;
  citations: boolean;
  extended_thinking: boolean;
  vision: boolean;
  attachments: string[];
}

export interface ProviderInfo {
  id: string;
  display_name: string;
  color: string;
  icon: string;
  enabled: boolean;
  models_by_mode: Record<string, ModelByMode>;
  configurable_parameters: ConfigurableParameter[];
  capabilities: Capabilities;
}

export interface ModeInfo {
  name: string;
  label: string;
  order: number;
  background: boolean;
  timeout_s: number | null;
}

export interface ProvidersResponse {
  providers: ProviderInfo[];
  default_system_prompts?: Record<string, string>;
  /** Optional while older servers are still supported. */
  modes?: ModeInfo[];
}

// --- Sessions ---

export interface ProviderResult {
  model: string;
  mode?: string;
  output?: string;
  error?: string;
  duration_ms?: number;
  metadata?: Record<string, unknown>;
  options?: Record<string, unknown>;
  usage?: Record<string, unknown> | null;
  meta?: string | null;
}

export interface SessionRecord {
  id: string;
  prompt: string;
  mode: string;
  targets: string[];
  providers: Record<string, ProviderResult>;
  created_at: string;
  created_by?: string;
  attachment_names?: string[];
  attachment_ids?: string[];
  judge_verdict?: JudgeResponse;
  /** Server-persisted auto-judge intent (v1.6) — present (true) for Send+Judge
   *  turns. Drives the reload-proof judge skeleton (pending = this && no verdict). */
  auto_judge_requested?: boolean;
  /** ISO-8601 UTC stamp of when a server-owned judge started (v1.6 #1). Lets a
   *  client reopening mid-judge show accurate elapsed time. iOS consumes this in
   *  the judging counter; web consumption is parity-pending. */
  judge_started_at?: string;
}

export interface SessionCreate {
  id?: string;
  title?: string;
  profile_id?: string;
  folder_id?: string;
  folder_context_version_id?: string;
}

export interface SessionUpdate {
  title?: string;
  flagged?: boolean;
  folder_id?: string;
  order?: number;
  profile_id?: string;
}

export interface SessionListItem {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  // Real "last chat date" (most recent message); not bumped by folder/flag/
  // rename touches. Recent sorts by this. Optional for backward compat.
  last_message_at?: string;
  flagged: boolean;
  folder_id?: string;
  order: number;
  record_count: number;
  shared: boolean;
  shared_by?: string;
  participants?: string[];
  owner_email?: string;
  // True when this session was created by a headless scheduled custom-query
  // run (drained into history on connect). Drives the small "scheduled" badge.
  from_schedule?: boolean;
  // Derived count of files attached across records in this session.
  attachment_count?: number;
}

export interface SessionListResponse {
  sessions: SessionListItem[];
  total: number;
  offset: number;
  limit: number;
}

export interface SessionResponse {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
  flagged: boolean;
  folder_id?: string;
  order: number;
  profile_id?: string;
  folder_context_version_id?: string;
  records: SessionRecord[];
  shared: boolean;
  owner_email?: string;
  participants?: Record<string, unknown>;
}

export interface RecordCreate {
  id?: string;
  prompt: string;
  mode: string;
  targets: string[];
  providers: Record<string, ProviderResult>;
  created_at?: string;
  attachment_names?: string[];
  attachment_ids?: string[];
}

// --- Chat ---

export interface ProviderRequest {
  provider: string;
  model: string;
  mode?: string;
  prompt?: string;
  options?: Record<string, unknown>;
  stream_idle_timeout_sec?: number;
  stream_total_timeout_sec?: number;
  conversation_history?: Array<{ role: string; content: string }>;
}

export interface ChatRequest {
  prompt: string;
  providers: ProviderRequest[];
  mode?: string;
  trace_id?: string;
  attachment_ids?: string[];
  folder_context?: string;
  session_id?: string;
  folder_context_version_id?: string;
  /** Set when the chat is a temp/disposable conversation so the server's
   *  admin_debug logs can flag it. Mirrors iOS isTemporary on /chat. */
  is_temporary?: boolean;
  /** Client-minted session record id sent up front so the server's
   *  /chat/stream + /chat/background runners can persist the completed
   *  result into sessions_{user}.json themselves (idempotent +
   *  upgrade-on-conflict on the matching client archive POST). Survives
   *  a tab close / network loss between all-providers-done and the
   *  client's archive call. Absent on temp chats (server-side persist
   *  is skipped without a session_id anyway). Mirrors iOS
   *  ChatRequest.recordId / ChatViewModel.persistedRecordIdForChat. */
  record_id?: string;
  /** Server-owned auto-judge (v1.6). True when the user fired Send + Judge so
   *  the server can own the verdict if the tab/app drops before the client
   *  fires /judge (bug #7). Mirrors iOS ChatRequest.auto_judge_requested.
   *  Only meaningful for persisted chats (server skips persist + judge for
   *  temp). Absent → server fallback does not fire (behavior unchanged). */
  auto_judge_requested?: boolean;
}

export interface ChatResponse {
  results: ProviderResponseResult[];
}

/** Server-driven client behavior config (v1.6). Mirrors server/client_config.py.
 *  Behavior only (feature kill-switches, timeouts, retry policy) — never UI
 *  structure, never gates offline capability. Fail-safe: an absent flag means
 *  the client keeps its shipped default (= enabled = today's behavior). */
export interface ClientConfig {
  config_version: number;
  features: Record<string, boolean>;
  timeouts: Record<string, number>;
  limits: Record<string, number>;
  retry: Record<string, unknown>;
  providers_disabled: string[];
}

// --- Server-delivered content ---

interface TextContentBlock {
  id: string;
  text: string;
  placeholders: string[];
}

/** Paragraph is optional for compatibility with the first content payload. */
export type ContentBlock = TextContentBlock & { type?: "paragraph" };

export type HeadingBlock = TextContentBlock & { type: "heading" };

export type CalloutBlock = TextContentBlock & { type: "callout"; style: "info" | "warning" };

export interface TableRow {
  id: string;
  cells: string[];
}

export interface TableBlock {
  id: string;
  type: "table";
  columns: string[];
  rows: TableRow[];
}

export interface ListBlock {
  id: string;
  type: "list";
  ordered: boolean;
  items: string[];
}

export type HelpBlock = ContentBlock | HeadingBlock | CalloutBlock | TableBlock | ListBlock;

export interface HelpDocument {
  blocks: HelpBlock[];
  columns: ContentColumn[];
}

export interface ContentColumn {
  id: string;
  label: string;
  align: string;
  width_hint: number;
}

export interface ContentDocument {
  blocks: ContentBlock[];
  columns: ContentColumn[];
}

export interface ContentResponse {
  version: string;
  documents: Record<string, ContentDocument>;
}

export interface ProviderResponseResult {
  provider: string;
  model: string;
  output?: string;
  error?: string;
  duration_ms?: number;
  usage?: Record<string, unknown> | null;
  meta?: string | null;
  options?: Record<string, unknown>;
}

export interface BackgroundStartResponse {
  job_id: string;
}

export interface BackgroundStatusResponse {
  status: "running" | "done" | "error";
  results?: ProviderResponseResult[];
  error?: string;
}

export interface DeepResearchBriefResponse {
  framing: string;
  sub_questions: string[];
  from_fallback: boolean;
  brief_text: string;
  expected_cost_low_usd: number;
  expected_cost_high_usd: number;
  expected_duration_seconds: number;
  stages?: DeepResearchStage[] | null;
}

export interface DeepResearchStage {
  number: number;
  label: string;
}

export interface DeepResearchBriefJobResponse {
  status: "running" | "done" | "error";
  result?: DeepResearchBriefResponse;
  error?: string;
}

export interface DeepResearchStartRequest {
  brief_text: string;
  question?: string;
  session_id: string;
  is_temporary?: boolean;
  /** Reusing this UUID after a lost response returns the original job. */
  client_request_id: string;
}

export interface DeepResearchSource {
  title: string;
  url: string;
  origin?: string;
  /** Citation marker supplied by the server, such as C1 or W3. */
  label?: string;
  /** Public publisher provenance, when the corpus record has one. */
  publisher_url?: string;
  /** Private document-server provenance, available only on the owner's network. */
  studio_url?: string;
  /** Supporting excerpt used to anchor the cited document. */
  passage?: string;
  /** Studio URL that opens at the supporting excerpt. */
  studio_open_url?: string;
  /** Original file type served by the Studio document server, when supplied. */
  media_kind?: string;
  /** Publication or show name, when the corpus record supplies it. */
  publication_label?: string;
}

export interface CorpusDocumentResponse {
  corpus_key: string;
  title: string;
  byte_length: number;
  copy: "live" | "archived";
  archived_at: string | null;
  /** Original file type served by the Studio document server, when supplied. */
  media_kind?: string;
  text: string;
}

export interface DeepResearchRunCounts {
  web_searches: number;
  library_documents_total: number;
  library_documents_read: number;
  library_facts: number;
}

export interface DeepResearchJobStatus {
  status: "running" | "done" | "cancelled" | "failed" | "error";
  iteration?: number;
  billable_search_count?: number;
  tool_counts?: Record<string, number>;
  run_counts?: DeepResearchRunCounts | null;
  cost_usd?: number;
  /** Optional: presence (including zero) means the run had a corpus licence. */
  corpus_documents_read?: number;
  corpus_documents_cited?: number;
  report?: string;
  sources?: DeepResearchSource[];
  model?: string;
  iterations?: number;
  error?: string;
  /** Present only for the staged team-engine Deep Research pipeline. */
  lane?: "full" | "aplus" | null;
  stages?: DeepResearchStage[] | null;
  stage?: number | null;
  stage_label?: string | null;
  stages_done?: number[] | null;
  stage_started_at?: string | null;
  detail?: string | null;
  early_look?: string | null;
  expected_finish_at?: string | null;
}

export interface DeepResearchActiveResponse {
  active: (DeepResearchJobStatus & {
    job_id: string;
    session_id: string | null;
    /** Server jobs use epoch seconds; ISO-8601 remains accepted for compatibility. */
    created_at: number | string | null;
    is_temporary: boolean;
    /** Memory-only question for an active non-temporary run. */
    question: string | null;
  }) | null;
}

/** Matches iOS's DeepResearchDisplayedToolCounts fallback order. */
export function getDeepResearchDisplayedToolCounts(
  status?: Pick<DeepResearchJobStatus, "tool_counts" | "billable_search_count">,
): { webSearchCount: number; corpusLookupCount: number } {
  return {
    webSearchCount: status?.tool_counts?.web_search ?? status?.billable_search_count ?? 0,
    corpusLookupCount: status?.tool_counts?.corpus_lookup ?? 0,
  };
}

// --- TTS ---
// Mirrors server/schemas/tts.py TTSRequest. Defaults intentionally match
// iOS TTSPlayer.swift (voice="alloy", model="gpt-4o-mini-tts") AND the
// server schema defaults — so the cache key derived from
// (text, voice, model, context) on the server hits the same entry both
// clients produce. Drift on any field = silent cache miss + duplicate
// OpenAI cost.

export type TTSContext = "one_off" | "voice_mode";

export interface TTSRequest {
  text: string;
  voice?: string;
  model?: string;
  context?: TTSContext;
}

// --- SSE Events ---

export type SSEEvent =
  | { type: "stream_id"; stream_id: string }
  | { type: "model_used"; provider: string; model: string }
  | { type: "delta"; provider: string; delta: string }
  | { type: "narration_boundary"; provider: string }
  | { type: "citations_replace"; provider: string; text: string }
  | {
      type: "done";
      provider: string;
      model: string;
      duration_ms: number;
      options?: Record<string, unknown>;
      usage?: Record<string, unknown> | null;
      meta?: string | null;
    }
  | { type: "error"; provider: string; error: string }
  | { type: "keepalive" };

// --- Preferences ---

export interface CustomInstructionsAlert {
  created_at: string;
  verdict: "accepted_as_is" | "modified" | "split_to_memory" | "rejected";
  summary: string;
  changes: string[];
  memories_added: string[];
  submitted_text: string;
  accepted_text: string;
}

export interface CustomInstructionItem {
  id: string;
  text: string;
  created_at: string;
}

export interface CustomInstructionsResponse {
  user_instructions: string;
  custom_instruction_items: CustomInstructionItem[];
  alert: CustomInstructionsAlert | null;
}

export interface UserPreferences {
  default_providers?: string[];
  provider_order?: string[];
  default_mode?: string;
  deep_research_use_library?: boolean;
  deep_research_library_available?: boolean;
  parameter_overrides?: Record<string, Record<string, Record<string, unknown>>>;
  system_prompts?: Record<string, string>;
  custom_instruction_items?: CustomInstructionItem[];
  custom_instructions_alert?: CustomInstructionsAlert | null;
}

// --- Folders ---

export interface FolderCreate {
  name: string;
  order?: number;
}

export interface FolderUpdate {
  name?: string;
  order?: number;
  separator_before?: boolean;
}

export interface FolderResponse {
  id: string;
  name: string;
  order: number;
  created_at: string;
  // Draw a horizontal grouping divider above this folder in every folder list.
  separator_before?: boolean;
}

/**
 * Canonical folder sort: by `order`, then a stable tiebreaker.
 *
 * `order` alone is NOT a stable sort key — when two folders share an
 * `order` value (which used to happen because the server trusted the
 * client-supplied `order` on create), `Array.sort` resolves the tie
 * arbitrarily, so the list reshuffles on its own across the 5s re-poll
 * and new folders appear mid-list. Tiebreak on `created_at` (older
 * first), then `id`, so equal-`order` folders always resolve the same
 * way. MUST match the iOS sort (SessionsViewModel folder sort) and be
 * used at every folder render site so they never diverge.
 */
export function compareFolders(a: FolderResponse, b: FolderResponse): number {
  return (
    a.order - b.order ||
    (a.created_at || "").localeCompare(b.created_at || "") ||
    a.id.localeCompare(b.id)
  );
}

// --- Custom Queries ---

/**
 * Optional auto-run schedule attached to a custom query. Snake_case on the
 * wire (matches the server contract). The server owns `next_run_at` /
 * `last_run_at` (computed on save + after each run); the client only writes
 * the user-facing fields. `day_of_week` is 0-6 with 0=Monday (weekly only);
 * `day_of_month` is 1-31 (monthly/quarterly only). `time_of_day` is 24h
 * "HH:MM" local to `timezone` (IANA).
 */
export interface QuerySchedule {
  enabled: boolean;
  frequency?: "daily" | "weekly" | "monthly" | "quarterly";
  time_of_day?: string;
  timezone?: string;
  day_of_week?: number | null;
  day_of_month?: number | null;
  next_run_at?: number | null;
  last_run_at?: number | null;
}

export interface CustomQueryCreate {
  name: string;
  system_prompt?: string;
  suggested_prompt?: string;
  default_mode?: string;
  folder_id?: string;
  order?: number;
  schedule?: QuerySchedule;
}

export interface CustomQueryUpdate {
  name?: string;
  system_prompt?: string;
  suggested_prompt?: string;
  default_mode?: string;
  folder_id?: string;
  order?: number;
  schedule?: QuerySchedule;
}

export interface CustomQueryResponse {
  id: string;
  name: string;
  system_prompt?: string;
  suggested_prompt?: string;
  default_mode?: string;
  folder_id?: string;
  order: number;
  created_at: string;
  schedule?: QuerySchedule;
}

// --- Memories ---

export interface MemoryResponse {
  id: string;
  content: string;
  created_at: string;
}

export interface MemoriesListResponse {
  memories: MemoryResponse[];
}

// --- Friends ---

export interface FriendRequestCreate {
  email: string;
}

export interface FriendRequestResponse {
  id: string;
  from_email: string;
  to_email: string;
  status: string;
  created_at: string;
  accepted_at?: string;
  to_name?: string;
}

export interface FriendResponse {
  id: string;
  email: string;
  display_name: string;
  created_at: string;
}

export interface FriendsListResponse {
  friends: FriendResponse[];
  pending_sent: FriendRequestResponse[];
  pending_received: FriendRequestResponse[];
}

// --- Sharing ---

export interface ShareSessionRequest {
  friend_ids: string[];
}

export interface SharedInboxItem {
  session_id: string;
  title: string;
  shared_by: string;
  last_activity_by?: string;
  unseen_count: number;
  last_activity_at?: string;
}

export interface SharedInboxResponse {
  total_unseen: number;
  items: SharedInboxItem[];
}

// --- Public share links (single-turn external links) ---
// Mirrors the server `ShareCreate` body and the iOS `ShareCreateBody`
// (see FaceOff/.../Networking/ShareService.swift). One latest chat turn
// is snapshotted into a public `trioai.co/s/<token>` link.

export interface ShareLinkProviderResponse {
  model?: string;
  output: string;
  duration_ms?: number;
  mode?: string;
  reasoning_effort?: string;
  thinking_level?: string;
  max_output_tokens?: number;
}

export interface ShareLinkProviderMeta {
  display_name: string;
  color: string;
}

export interface ShareLinkCreateRequest {
  prompt: string;
  mode: string;
  title?: string;
  provider_order: string[];
  responses: Record<string, ShareLinkProviderResponse>;
  providers_meta: Record<string, ShareLinkProviderMeta>;
  // The server CLAMPS non-admin callers to 24h non-permanent regardless
  // of what is sent. Admin callers are honored. Default: 24h.
  permanent?: boolean;
  expiry_days?: number;
}

export interface ShareLinkCreateResponse {
  token: string;
  url: string;
  // null for a permanent link.
  expires_at: string | null;
}

// --- Public share links (whole multi-turn session) ---
// Mirrors the server `POST /shares/session` body. The server snapshots
// the entire saved session itself (all records + each record's judge
// verdict + provider meta), so the client only needs the session id.
// Same lifetime semantics as ShareLinkCreateRequest: non-admin callers
// are clamped to 24h non-permanent server-side; admin callers honored.

export interface SessionShareLinkCreateRequest {
  session_id: string;
  title?: string;
  permanent: boolean;
  expiry_days: number;
}

export interface SessionShareLinkCreateResponse {
  token: string;
  url: string;
  // null for a permanent link.
  expires_at: string | null;
}

// --- Batch ---

export interface BatchRequest {
  action: "delete" | "flag" | "unflag" | "move";
  session_ids: string[];
  folder_id?: string;
}

// --- Attachments ---

export interface AttachmentResponse {
  id: string;
  filename: string;
  mime_type: string;
  size_bytes: number;
  created_at: string;
  // Set when server auto-compressed a PDF (>20 MB upload). Holds the
  // original upload size so the UI can render a "compressed N → M MB" notice.
  original_size_bytes?: number | null;
  // PDF text-density stats from server-side PyMuPDF extraction. Auto-mode
  // classifier reads these to route on actual content density. None for
  // non-PDF types or extraction failure.
  text_chars?: number | null;
  page_count?: number | null;
  sampled_pages?: number | null;
}

export interface AttachmentUploadResponse {
  attachments: AttachmentResponse[];
}

// --- Folder Context ---

export interface FolderContext {
  version_id: string;
  timestamp: string;
  summary: string;
}

export interface FolderMemoryItem {
  id: string;
  type: "constraint" | "preference" | "decision" | "entity" | "open_thread";
  text: string;
  date: string;
  source_session_id: string;
}

export interface FolderMemoryResponse {
  items: FolderMemoryItem[];
  updated_at: string | null;
}

// --- Summarize ---

export interface SummaryResponse {
  summary: string;
}

// --- Judge ---

export interface JudgeResponse {
  winner: string;
  rationale: string;
  synthesis: string;
  scores?: Record<string, number>;
  usage?: Record<string, unknown>;
  scoring_provider?: string | null;
  // 2026-05-03 v1.1 additions — surfaced from /judge SSE events.
  auto?: boolean;
  scoring_model?: string;
  synthesis_model?: string;
  scoring_duration_ms?: number;
  synthesis_duration_ms?: number;
  total_duration_ms?: number;
  synthesis_max_tokens?: number;
  synthesis_budget_tokens?: number;
  // 2026-05-05 panel scoring — all three providers score in parallel,
  // scores are averaged. `scoring_provider` carries the credited rationale's
  // scorer (legacy compat). The arrays below carry the full panel for UI
  // transparency: "Judges: Anthropic, Gemini, OpenAI" instead of a single
  // scorer name. `panel_scores` is the per-scorer breakdown.
  panel_scoring_providers?: string[];
  panel_scoring_models?: string[];
  panel_scores?: Record<string, Record<string, number>>;
  // 2026-05-09 tie rule — server emits all responders that are EXACTLY
  // tied at the top mean. Single-winner verdicts still set
  // winners=[winner] for shape consistency. UI should treat any
  // multi-element `winners` as a real tie ("Tied: X & Y"). Tie-with-
  // OpenAI is suppressed server-side, so OAI in a multi-element
  // `winners` should never happen in practice.
  winners?: string[];
}

export interface JudgeInlineProvider {
  output: string;
  usage?: Record<string, unknown> | null;
}

export interface JudgeInlineRecord {
  prompt: string;
  providers: Record<string, JudgeInlineProvider>;
  judge_verdict?: JudgeResponse | null;
}

export interface JudgeRequest {
  session_id?: string | null;
  record_index?: number | null;
  scoring_provider?: string | null;
  records?: JudgeInlineRecord[] | null;
  is_auto?: boolean;
}

// --- Auto mode classifier ---

export interface ClassifyAttachment {
  filename: string;
  mime_type: string;
  size_bytes: number;
  text_chars?: number;
  page_count?: number;
  sampled_pages?: number;
}

export interface ConversationTurn {
  role: string;
  content: string;
}

export interface ClassifyRequest {
  prompt: string;
  targets: string[];
  history?: ConversationTurn[];
  attachments?: ClassifyAttachment[];
  custom_query_prompt?: string;
}

export interface ClassifyDecision {
  mode: string;
  params?: Record<string, unknown>;
}

export interface ClassifyResponse {
  decisions: Record<string, ClassifyDecision>;
}

// =============================================================================
// Internal UI Types — camelCase (not from server)
// =============================================================================

export interface StreamingResponse {
  id: string;
  text: string;
  isComplete: boolean;
  error?: string;
  model?: string;
  mode?: string;
  durationMs?: number;
  streamId?: string;
  backgroundJobId?: string;
  wasInterrupted: boolean;
  options?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  /** Opaque server-normalized counts, persisted without client interpretation. */
  usage?: Record<string, unknown> | null;
  /** Server-authored provider metadata, rendered verbatim. */
  meta?: string | null;
  // Live UI only. useChat deliberately never puts this in its streaming
  // bucket, records, sessions, or LocalCache.
  narrationStatus?: string;
}

export interface ChatRecord {
  prompt: string;
  mode: string;
  responses: Record<string, StreamingResponse>;
  providerOrder: string[];
  timestamp: Date;
  serverRecordId?: string;
  attachmentNames?: string[];
  attachmentIds?: string[];
  judgeVerdict?: JudgeResponse;
  /** Server-authoritative ISO-8601 judge start stamp for the in-flight clock. */
  judgeStartedAt?: string;
  /** Server-persisted auto-judge intent (v1.6). True when this turn was a
   *  Send+Judge. Lets the judge skeleton render on a fresh load/reload (no
   *  client state needed): auto_judge_requested && !judgeVerdict = pending. */
  autoJudgeRequested?: boolean;
  /**
   * Email of the user who authored this record. Populated by the server on
   * shared-session reads so the UI can label follow-ups sent by other
   * participants. Absent on records in non-shared sessions.
   */
  createdBy?: string;
}

// --- Admin Badges ---

export interface AdminBadgeCounts {
  feedback: number;
  exports: number;
  usage: number;
  total: number;
}

// --- Admin Pricing Proposals ---

export interface PricingProposalItem {
  provider: string;
  mode: string;
  model: string;
  current_input: number;
  current_output: number;
  proposed_input: number;
  proposed_output: number;
  source_url: string;
  confidence: "high" | "medium" | "low";
  reasoning: string;
}

export interface PricingProposal {
  id: string;
  created_at: string;
  items: PricingProposalItem[];
  applied_items?: PricingProposalItem[];
  last_applied_at?: string;
}

export interface PricingProposalsResponse {
  proposals: PricingProposal[];
  current_rates: Record<string, unknown>;
  pricing_metadata: {
    version: number;
    effective_from: string;
  };
}

export interface PricingProposalApprovedItem {
  provider: string;
  mode: string;
}

// --- Admin Model Proposals (daily check_new_models.py output) ---

export interface ModelProposalItem {
  provider: string;       // "anthropic" | "openai" | "gemini"
  model_id: string;       // raw provider id, e.g. "claude-opus-4-8-20260601"
  family?: string;        // human label, e.g. "Opus 4.8"
  category?: string;      // "chat" | "judge" | "tts" | "classify" | "other"
  current_models?: string;// what Trio uses today on this surface
  upgrade_reason?: string;// 1-2 sentences from Haiku classifier
  source_signal?: string; // provenance breadcrumb
  upgrade_priority?: "high" | "medium" | "low"; // sort + render hint
}

export interface ModelProposal {
  id: string;
  created_at: string;
  status?: "pending" | "dismissed";
  items: ModelProposalItem[];
  dismissed_at?: string;
}

export interface ModelProposalsResponse {
  proposals: ModelProposal[];
}

// --- Search ---

export interface SearchTitleMatch {
  id: string;
  title: string;
  updated_at: string;
}

export interface SearchContentMatch {
  id: string;
  title: string;
  updated_at: string;
  snippet: string;
}

export interface SearchResponse {
  title_matches: SearchTitleMatch[];
  content_matches: SearchContentMatch[];
}
